import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RealtimeChannel } from '@supabase/supabase-js';
import { useAuthStore } from '../../store/useAuthStore';
import { supabase } from '../../lib/supabaseClient';
import {
  Channel,
  Message,
  getChannels,
  getMessages,
  sendMessage,
  editMessage,
  deleteMessage,
  markChannelAsRead,
  getCollaborators,
  getOrCreateDmChannel,
  createChannel,
  subscribeToMessages,
  unsubscribeFromMessages,
  getDmOtherLastReadAt,
  subscribeToChannelMembers,
  refreshCollaboratorCache,
  updateChannel,
  archiveChannel,
  deleteChannel,
  getChannelMembers,
  addChannelMember,
  removeChannelMember,
  getDossierChannel,
  getChannelUnreadCount,
  getDossierChannelMembers,
  getPinnedMessages,
  setMessagePinned,
  inviteManualMember,
  removeManualMember,
  type CollaboratorSummary,
  type UpdateChannelParams,
  type DossierChannelMember,
} from '../../services/messaging/messagingService';
import {
  uploadMessageAttachment,
  getAttachmentsForMessages,
  deleteMessageAttachment,
  type MessageAttachment,
} from '../../services/messaging/messageAttachmentService';
import {
  getReactionsForMessages,
  addReaction,
  removeReaction,
  type MessageReaction,
} from '../../services/messaging/messageReactionService';
import { LIVE, STATIC } from '../../lib/queryPresets';

// ============================================================
// Query keys
// ============================================================

export const messagingKeys = {
  channels: () => ['messaging', 'channels'] as const,
  messages: (channelId: string) => ['messaging', 'messages', channelId] as const,
  members: (channelId: string) => ['messaging', 'members', channelId] as const,
  collaborators: () => ['messaging', 'collaborators'] as const,
  dmReadReceipt: (channelId: string) => ['messaging', 'dmReadReceipt', channelId] as const,
  dossierChannel: (dossierId: string) => ['messaging', 'dossierChannel', dossierId] as const,
  channelUnread: (channelId: string) => ['messaging', 'channelUnread', channelId] as const,
  dossierMembers: (channelId: string) =>
    ['messaging', 'dossierMembers', channelId] as const,
  attachments: (channelId: string) =>
    ['messaging', 'attachments', channelId] as const,
  reactions: (channelId: string) =>
    ['messaging', 'reactions', channelId] as const,
  pinned: (channelId: string) => ['messaging', 'pinned', channelId] as const,
};

// ============================================================
// DM read receipts (✓✓)
// ============================================================

export function useDmReadReceipt(channelId: string | null, enabled: boolean): string | null {
  const qc = useQueryClient();
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const session = useAuthStore((s) => s.session);

  const { data } = useQuery({
    ...STATIC, // Realtime (channel messages) → pas de polling, le push fait foi
    queryKey: channelId ? messagingKeys.dmReadReceipt(channelId) : ['messaging', 'dmReadReceipt', 'none'],
    queryFn: () => (channelId ? getDmOtherLastReadAt(channelId) : Promise.resolve(null)),
    enabled: !!channelId && enabled && isInitialized && !!session,
  });

  useEffect(() => {
    if (!channelId || !enabled || !isInitialized || !session) return;
    const sub = subscribeToChannelMembers(channelId, () => {
      qc.invalidateQueries({ queryKey: messagingKeys.dmReadReceipt(channelId) });
    });
    return () => {
      supabase.removeChannel(sub);
    };
  }, [channelId, enabled, isInitialized, session, qc]);

  return data ?? null;
}

// ============================================================
// Hooks — Channels
// ============================================================

export function useChannels() {
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const session = useAuthStore((s) => s.session);

  return useQuery({
    // Le Realtime messagerie n'est monté que sur un canal OUVERT (ChannelView) : sans polling,
    // le badge non-lu resterait figé pour les canaux non ouverts. D'où LIVE et pas STATIC.
    ...LIVE,
    queryKey: messagingKeys.channels(),
    queryFn: getChannels,
    enabled: isInitialized && !!session,
  });
}

export function useTotalUnreadCount(): number {
  const { data: channels } = useChannels();
  if (!channels) return 0;
  return channels.reduce((sum, ch) => sum + (ch.unreadCount ?? 0), 0);
}

// ============================================================
// Hooks — Messages
// ============================================================

/** Taille d'un lot de messages (chargement initial ET pagination vers le haut). */
export const MESSAGES_PAGE_SIZE = 50;

export function useMessages(channelId: string | null) {
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const session = useAuthStore((s) => s.session);

  return useQuery({
    ...STATIC, // Realtime (channel messages) → pas de polling, le push fait foi
    queryKey: messagingKeys.messages(channelId ?? ''),
    queryFn: () => getMessages(channelId!, MESSAGES_PAGE_SIZE),
    enabled: isInitialized && !!session && !!channelId,
  });
}

/**
 * Pagination vers le haut : charge le lot de messages précédant le plus ancien
 * déjà en cache et le PRÉFIXE à la liste (même queryKey que `useMessages`, donc
 * compatible avec le Realtime qui ajoute en bas). Dédup par id.
 *
 * - `hasMore` : false dès qu'un lot revient incomplet (< MESSAGES_PAGE_SIZE) → début du canal.
 * - `loadOlder()` : no-op si déjà en cours, si plus rien à charger, ou si le cache est vide.
 */
export function useLoadOlderMessages(channelId: string | null) {
  const qc = useQueryClient();
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  // Réinitialise l'état "hasMore" quand on change de canal.
  useEffect(() => {
    setHasMore(true);
    setIsLoadingOlder(false);
  }, [channelId]);

  const loadOlder = useCallback(async () => {
    if (!channelId || isLoadingOlder || !hasMore) return;

    const current = qc.getQueryData<Message[]>(messagingKeys.messages(channelId));
    const oldest = current?.[0];
    if (!oldest) return; // rien à paginer avant le 1er fetch

    setIsLoadingOlder(true);
    try {
      const older = await getMessages(channelId, MESSAGES_PAGE_SIZE, oldest.createdAt);
      if (older.length < MESSAGES_PAGE_SIZE) setHasMore(false);
      if (older.length > 0) {
        qc.setQueryData<Message[]>(messagingKeys.messages(channelId), (prev) => {
          if (!prev) return older;
          const seen = new Set(prev.map((m) => m.id));
          const fresh = older.filter((m) => !seen.has(m.id));
          return [...fresh, ...prev]; // préfixe les anciens, garde l'ordre chronologique
        });
      }
    } finally {
      setIsLoadingOlder(false);
    }
  }, [channelId, isLoadingOlder, hasMore, qc]);

  return { loadOlder, isLoadingOlder, hasMore };
}

// ============================================================
// Hooks — Collaborators
// ============================================================

export function useCollaboratorsForMessaging() {
  const isInitialized = useAuthStore((s) => s.isInitialized);

  return useQuery({
    ...STATIC, // Realtime (channel messages) → pas de polling, le push fait foi
    queryKey: messagingKeys.collaborators(),
    queryFn: async () => {
      const collabs = await getCollaborators();
      // Warm the mention cache at the same time
      await refreshCollaboratorCache();
      return collabs;
    },
    enabled: isInitialized,
  });
}

// ============================================================
// Mutations
// ============================================================

export function useSendMessage() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      channelId,
      body,
      parentId,
      attachments,
    }: {
      channelId: string;
      body: string;
      parentId?: string;
      attachments?: File[];
    }) => {
      const hasAttachments = !!attachments && attachments.length > 0;
      const message = await sendMessage(channelId, body, parentId, hasAttachments);

      if (attachments && attachments.length > 0) {
        const uploaderId = useAuthStore.getState().collaborator?.id;
        if (!uploaderId) throw new Error('Not authenticated');
        const uploaded: MessageAttachment[] = [];
        for (const file of attachments) {
          const att = await uploadMessageAttachment(message.id, file, uploaderId);
          uploaded.push(att);
        }
        return { message, attachments: uploaded };
      }
      return { message, attachments: [] as MessageAttachment[] };
    },

    onSuccess: ({ message, attachments: uploaded }) => {
      qc.setQueryData<Message[]>(
        messagingKeys.messages(message.channelId),
        (prev) => (prev ? [...prev, message] : [message]),
      );
      if (uploaded.length > 0) {
        qc.setQueryData<Map<string, MessageAttachment[]>>(
          messagingKeys.attachments(message.channelId),
          (prev) => {
            const next = new Map(prev ?? []);
            next.set(message.id, uploaded);
            return next;
          },
        );
      }
      qc.invalidateQueries({ queryKey: messagingKeys.channels() });
    },
  });
}

// ============================================================
// Attachments
// ============================================================

export function useMessageAttachments(
  channelId: string | null,
  messageIds: string[],
) {
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const session = useAuthStore((s) => s.session);

  return useQuery({
    ...STATIC, // Realtime (channel messages) → pas de polling, le push fait foi
    queryKey: channelId
      ? [...messagingKeys.attachments(channelId), messageIds.length]
      : ['messaging', 'attachments', 'none'],
    queryFn: () => getAttachmentsForMessages(messageIds),
    enabled: isInitialized && !!session && !!channelId && messageIds.length > 0,
  });
}

export function useDeleteMessageAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      attachmentId,
      storagePath,
      channelId,
      messageId,
    }: {
      attachmentId: string;
      storagePath: string;
      channelId: string;
      messageId: string;
    }) =>
      deleteMessageAttachment(attachmentId, storagePath).then(() => ({
        attachmentId,
        channelId,
        messageId,
      })),
    onSuccess: ({ attachmentId, channelId, messageId }) => {
      qc.setQueryData<Map<string, MessageAttachment[]>>(
        messagingKeys.attachments(channelId),
        (prev) => {
          if (!prev) return prev;
          const next = new Map(prev);
          const list = next.get(messageId);
          if (list) {
            next.set(messageId, list.filter((a) => a.id !== attachmentId));
          }
          return next;
        },
      );
    },
  });
}

export type { MessageAttachment };

// ============================================================
// Reactions
// ============================================================

export function useMessageReactions(
  channelId: string | null,
  messageIds: string[],
) {
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const session = useAuthStore((s) => s.session);

  return useQuery({
    ...STATIC, // Realtime (channel messages) → pas de polling, le push fait foi
    queryKey: channelId
      ? [...messagingKeys.reactions(channelId), messageIds.length]
      : ['messaging', 'reactions', 'none'],
    queryFn: () => getReactionsForMessages(messageIds),
    enabled: isInitialized && !!session && !!channelId && messageIds.length > 0,
  });
}

export function useToggleReaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      messageId,
      emoji,
      channelId: _channelId,
      hasReacted,
    }: {
      messageId: string;
      emoji: string;
      channelId: string;
      hasReacted: boolean;
    }) => {
      const collaboratorId = useAuthStore.getState().collaborator?.id;
      if (!collaboratorId) throw new Error('Not authenticated');
      if (hasReacted) {
        await removeReaction(messageId, emoji, collaboratorId);
      } else {
        await addReaction(messageId, emoji, collaboratorId);
      }
      return { collaboratorId };
    },
    onSuccess: (_data, { channelId }) => {
      qc.invalidateQueries({ queryKey: messagingKeys.reactions(channelId) });
    },
  });
}

export function useReactionsRealtime(channelId: string | null) {
  const qc = useQueryClient();
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const session = useAuthStore((s) => s.session);

  useEffect(() => {
    if (!isInitialized || !session || !channelId) return;

    const ch = supabase
      .channel(`message_reactions:${channelId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'message_reactions' },
        () => {
          qc.invalidateQueries({ queryKey: messagingKeys.reactions(channelId) });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(ch);
    };
  }, [channelId, isInitialized, session, qc]);
}

export type { MessageReaction };

/**
 * Realtime sur message_attachments : un ajout/suppression de PJ par un autre
 * utilisateur invalide le cache des PJ du canal (sinon il faut refresh).
 */
export function useAttachmentsRealtime(channelId: string | null) {
  const qc = useQueryClient();
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const session = useAuthStore((s) => s.session);

  useEffect(() => {
    if (!isInitialized || !session || !channelId) return;

    const ch = supabase
      .channel(`message_attachments:${channelId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'message_attachments' },
        () => {
          qc.invalidateQueries({ queryKey: messagingKeys.attachments(channelId) });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(ch);
    };
  }, [channelId, isInitialized, session, qc]);
}

export function useEditMessage() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body, channelId }: { id: string; body: string; channelId: string }) =>
      editMessage(id, body).then(() => ({ id, body, channelId })),

    onSuccess: ({ id, body, channelId }) => {
      qc.setQueryData<Message[]>(messagingKeys.messages(channelId), (prev) =>
        prev
          ? prev.map((m) =>
              m.id === id
                ? { ...m, body, isEdited: true, editedAt: new Date().toISOString() }
                : m,
            )
          : prev,
      );
    },
  });
}

export function useDeleteMessage() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, channelId }: { id: string; channelId: string }) =>
      deleteMessage(id).then(() => ({ id, channelId })),

    onSuccess: ({ id, channelId }) => {
      qc.setQueryData<Message[]>(messagingKeys.messages(channelId), (prev) =>
        prev
          ? prev.map((m) =>
              m.id === id ? { ...m, deletedAt: new Date().toISOString() } : m,
            )
          : prev,
      );
    },
  });
}

export function useMarkChannelAsRead() {
  const qc = useQueryClient();
  return useCallback((channelId: string) => {
    markChannelAsRead(channelId);
    // Zero out unread count in cache immediately so the sidebar badge disappears
    qc.setQueryData<Channel[]>(messagingKeys.channels(), (prev) =>
      prev?.map((ch) => ch.id === channelId ? { ...ch, unreadCount: 0 } : ch),
    );
    qc.setQueryData(messagingKeys.channelUnread(channelId), 0);
  }, [qc]);
}

export function useCreateChannel() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ name, isPrivate, description, emoji }: { name: string; isPrivate?: boolean; description?: string; emoji?: string }) =>
      createChannel(name, isPrivate, description, emoji),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: messagingKeys.channels() });
    },
  });
}

export function useGetOrCreateDm() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (otherCollaboratorId: string) => getOrCreateDmChannel(otherCollaboratorId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: messagingKeys.channels() });
    },
  });
}

// ============================================================
// Realtime — per channel subscription
// ============================================================

export function useMessagingRealtime(channelId: string | null) {
  const qc = useQueryClient();
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const session = useAuthStore((s) => s.session);
  const currentCollaboratorId = useAuthStore((s) => s.collaborator?.id);
  const channelRef = useRef<RealtimeChannel | null>(null);

  useEffect(() => {
    if (!isInitialized || !session || !channelId) return;

    channelRef.current = subscribeToMessages(channelId, (message) => {
      qc.setQueryData<Message[]>(messagingKeys.messages(channelId), (prev) => {
        if (!prev) return [message];
        // Avoid duplicates (may arrive from both optimistic update + realtime)
        if (prev.find((m) => m.id === message.id)) {
          // Update (edit or soft delete)
          return prev.map((m) => (m.id === message.id ? message : m));
        }
        // New message — skip own (already added optimistically)
        if (message.authorId === currentCollaboratorId) return prev;
        return [...prev, message];
      });

      // Update unread badge for other collaborators
      if (message.authorId !== currentCollaboratorId) {
        qc.invalidateQueries({ queryKey: messagingKeys.channels() });
        // Refetch attachments map to pick up PJ for this new message
        qc.invalidateQueries({ queryKey: messagingKeys.attachments(channelId) });
      }
    });

    return () => {
      if (channelRef.current) {
        unsubscribeFromMessages(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [channelId, isInitialized, session, currentCollaboratorId, qc]);
}

// ============================================================
// Cache helpers (exported for SidebarNav badge)
// ============================================================

export function useMessagingInvalidate() {
  const qc = useQueryClient();
  return {
    invalidateChannels: useCallback(
      () => qc.invalidateQueries({ queryKey: messagingKeys.channels() }),
      [qc],
    ),
    invalidateMessages: useCallback(
      (channelId: string) =>
        qc.invalidateQueries({ queryKey: messagingKeys.messages(channelId) }),
      [qc],
    ),
  };
}

// ============================================================
// Channel management hooks
// ============================================================

export function useChannelMembers(channelId: string | null) {
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const session = useAuthStore((s) => s.session);

  return useQuery({
    ...STATIC, // Realtime (channel messages) → pas de polling, le push fait foi
    queryKey: messagingKeys.members(channelId ?? ''),
    queryFn: () => getChannelMembers(channelId!),
    enabled: isInitialized && !!session && !!channelId,
  });
}

export function useUpdateChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, params }: { id: string; params: UpdateChannelParams }) =>
      updateChannel(id, params),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: messagingKeys.channels() });
    },
  });
}

export function useArchiveChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => archiveChannel(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: messagingKeys.channels() });
    },
  });
}

export function useDeleteChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteChannel(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: messagingKeys.channels() });
    },
  });
}

export function useAddChannelMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ channelId, collaboratorId }: { channelId: string; collaboratorId: string }) =>
      addChannelMember(channelId, collaboratorId),
    onSuccess: (_data, { channelId }) => {
      qc.invalidateQueries({ queryKey: messagingKeys.members(channelId) });
    },
  });
}

export function useRemoveChannelMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ channelId, collaboratorId }: { channelId: string; collaboratorId: string }) =>
      removeChannelMember(channelId, collaboratorId),
    onSuccess: (_data, { channelId }) => {
      qc.invalidateQueries({ queryKey: messagingKeys.members(channelId) });
    },
  });
}

// ============================================================
// Typing indicator — broadcast-based
// ============================================================

interface TypingPresence {
  typingUsers: string[];
  startTyping: () => void;
  stopTyping: () => void;
}

type TypingEntry = { name: string; ts: number };

export function useTypingPresence(channelId: string | null): TypingPresence {
  const [typingMap, setTypingMap] = useState<Record<string, TypingEntry>>({});
  const collaborator = useAuthStore((s) => s.collaborator);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!channelId) return;

    const ch = supabase
      .channel(`typing:${channelId}`)
      .on(
        'broadcast',
        { event: 'typing' },
        ({ payload }: { payload: { id: string; name: string; typing: boolean } }) => {
          if (payload.id === collaborator?.id) return;
          setTypingMap((prev) => {
            if (!payload.typing) {
              const next = { ...prev };
              delete next[payload.id];
              return next;
            }
            return { ...prev, [payload.id]: { name: payload.name, ts: Date.now() } };
          });
        },
      )
      .subscribe();

    channelRef.current = ch;

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      supabase.removeChannel(ch);
      channelRef.current = null;
      setTypingMap({});
    };
  }, [channelId, collaborator?.id]);

  // Expire stale entries after 5s (in case stop broadcast was missed)
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setTypingMap((prev) => {
        const next = Object.fromEntries(
          Object.entries(prev).filter(([, v]) => now - v.ts < 5000),
        );
        return Object.keys(next).length === Object.keys(prev).length ? prev : next;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const stopTyping = useCallback(() => {
    if (!collaborator || !channelRef.current) return;
    channelRef.current.send({
      type: 'broadcast',
      event: 'typing',
      payload: { id: collaborator.id, name: collaborator.full_name, typing: false },
    });
    if (timerRef.current) clearTimeout(timerRef.current);
  }, [collaborator]);

  const startTyping = useCallback(() => {
    if (!collaborator || !channelRef.current) return;
    channelRef.current.send({
      type: 'broadcast',
      event: 'typing',
      payload: { id: collaborator.id, name: collaborator.full_name, typing: true },
    });
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(stopTyping, 3000);
  }, [collaborator, stopTyping]);

  const typingUsers = Object.values(typingMap).map((e) => e.name);

  return { typingUsers, startTyping, stopTyping };
}

// ============================================================
// Dossier channels (EPIC G-07)
// ============================================================

export function useDossierChannel(dossierId: string | undefined) {
  return useQuery({
    ...STATIC, // Realtime (channel messages) → pas de polling, le push fait foi
    queryKey: dossierId ? messagingKeys.dossierChannel(dossierId) : ['messaging', 'dossierChannel', 'none'],
    queryFn: () => getDossierChannel(dossierId!),
    enabled: !!dossierId,
  });
}

/** Non-lus du salon dossier. `viewing` = onglet Discussions ouvert → pastille à 0. */
export function useDossierDiscussionUnread(
  dossierId: string | undefined,
  opts?: { viewing?: boolean },
): number {
  const { data: channel } = useDossierChannel(dossierId);
  const channelId = channel?.id;
  const qc = useQueryClient();
  const currentId = useAuthStore((s) => s.collaborator?.id);
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const session = useAuthStore((s) => s.session);
  const viewing = opts?.viewing ?? false;

  const { data: unread = 0 } = useQuery({
    ...LIVE,
    queryKey: channelId
      ? messagingKeys.channelUnread(channelId)
      : ['messaging', 'channelUnread', 'none'],
    queryFn: () => getChannelUnreadCount(channelId!),
    enabled: !!channelId && isInitialized && !!session,
  });

  useEffect(() => {
    if (!channelId || !isInitialized || !session) return;

    const sub = supabase
      .channel(`dossier-unread:${channelId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `channel_id=eq.${channelId}`,
        },
        (payload) => {
          const row = payload.new as { author_id?: string; deleted_at?: string | null };
          if (row.deleted_at) return;
          if (row.author_id === currentId) return;
          if (viewing) {
            qc.setQueryData(messagingKeys.channelUnread(channelId), 0);
            return;
          }
          qc.setQueryData<number>(
            messagingKeys.channelUnread(channelId),
            (prev) => (prev ?? 0) + 1,
          );
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(sub);
    };
  }, [channelId, isInitialized, session, currentId, viewing, qc]);

  return viewing ? 0 : unread;
}

/**
 * Lecture seule du même compteur, pour un second affichage sur la fiche
 * dossier (raccourcis de l'aperçu). L'abonnement temps réel reste porté par
 * `useDossierDiscussionUnread` : un deuxième abonnement au même canal plante.
 */
export function useDossierDiscussionUnreadCount(dossierId: string | undefined): number {
  const { data: channel } = useDossierChannel(dossierId);
  const channelId = channel?.id;
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const session = useAuthStore((s) => s.session);

  const { data: unread = 0 } = useQuery({
    ...LIVE,
    queryKey: channelId
      ? messagingKeys.channelUnread(channelId)
      : ['messaging', 'channelUnread', 'none'],
    queryFn: () => getChannelUnreadCount(channelId!),
    enabled: !!channelId && isInitialized && !!session,
  });

  return unread;
}

export function useDossierChannelMembers(channelId: string | undefined) {
  return useQuery({
    ...STATIC, // Realtime (channel messages) → pas de polling, le push fait foi
    queryKey: channelId ? messagingKeys.dossierMembers(channelId) : ['messaging', 'dossierMembers', 'none'],
    queryFn: () => getDossierChannelMembers(channelId!),
    enabled: !!channelId,
  });
}

export function useInviteManualMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ channelId, collaboratorId }: { channelId: string; collaboratorId: string }) =>
      inviteManualMember(channelId, collaboratorId),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: messagingKeys.dossierMembers(vars.channelId) });
      qc.invalidateQueries({ queryKey: messagingKeys.channels() });
    },
  });
}

export function useRemoveManualMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ channelId, collaboratorId }: { channelId: string; collaboratorId: string }) =>
      removeManualMember(channelId, collaboratorId),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: messagingKeys.dossierMembers(vars.channelId) });
      qc.invalidateQueries({ queryKey: messagingKeys.channels() });
    },
  });
}

// Re-export types for convenience
export type { Channel, Message, CollaboratorSummary, DossierChannelMember };

// ============================================================
// Messages épinglés
// ============================================================

export function usePinnedMessages(channelId: string | undefined) {
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const session = useAuthStore((s) => s.session);
  return useQuery({
    ...LIVE,
    queryKey: channelId ? messagingKeys.pinned(channelId) : ['messaging', 'pinned', 'none'],
    queryFn: () => getPinnedMessages(channelId!),
    enabled: !!channelId && isInitialized && !!session,
  });
}

export function useSetMessagePinned() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, pinned }: { id: string; channelId: string; pinned: boolean }) =>
      setMessagePinned(id, pinned),
    onSuccess: (_, { id, channelId, pinned }) => {
      qc.setQueryData<Message[]>(messagingKeys.messages(channelId), (prev) =>
        prev
          ? prev.map((m) => (m.id === id ? { ...m, pinnedAt: pinned ? new Date().toISOString() : null } : m))
          : prev,
      );
      qc.invalidateQueries({ queryKey: messagingKeys.pinned(channelId) });
    },
  });
}
